import * as React from 'react'

import { action, computed, makeObservable, observable } from 'mobx'
import { observer } from 'mobx-react'

import PermitWorkflowDiagram from '~/client/src/desktop/components/WorkflowDiagrams/PermitWorkflowDiagram/PermitWorkflowDiagram'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/client/src/shared/components/shadcn-ui/dialog'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import StruxhubSelect from '~/client/src/shared/components/StruxhubInputs/StruxhubSelect'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import WorkflowType from '~/client/src/shared/models/WorkflowType'
import { EMPTY_STRING } from '~/client/src/shared/utils/usefulStrings'

import PermitsSetUpStore from '../PermitsSetUp.store'

interface IProps {
  store: PermitsSetUpStore
}

@observer
export default class NewFormTypeDialog extends React.Component<IProps> {
  @observable private typeName: string = EMPTY_STRING
  @observable private basedOn: string = EMPTY_STRING
  @observable private categoryId: string = EMPTY_STRING
  @observable private store: PermitsSetUpStore = null

  public constructor(props: IProps) {
    super(props)

    this.store = props.store
    makeObservable(this)
  }

  public render() {
    const { store } = this.props
    const { activeCategories, hideNewFormTypeDialog: hideNewTypeForm } = store

    return (
      <Dialog
        open
        onOpenChange={(open, { reason }) =>
          !open && reason === 'close-press' && hideNewTypeForm()
        }
      >
        <DialogContent className="tw:flex tw:max-h-[85vh] tw:w-[60%] tw:flex-col tw:sm:max-w-[60%]">
          <DialogHeader>
            <DialogTitle>
              {Localization.translator.createNewWorkflow}
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-auto relative tw:min-h-0 tw:flex-1">
            <div className="row x-center">
              <div className="col tw:max-w-[400px]">
                <StruxhubInput
                  label={Localization.translator.enterWorkflowType}
                  isRequired={true}
                  value={this.typeName}
                  onChange={this.onNameChanged}
                  onValueReset={this.onNameReset}
                />
                <StruxhubSelect
                  label={Localization.translator.workflowCategory}
                  isRequired
                  value={this.categoryId}
                  onChange={this.onCategoryChanged}
                >
                  <option></option>
                  {activeCategories.map(category => (
                    <option
                      key={category.id}
                      value={category.id}
                      className="text large"
                    >
                      {category.name}
                    </option>
                  ))}
                </StruxhubSelect>
                <StruxhubSelect
                  label={Localization.translator.basedOnWorkflow}
                  isRequired={true}
                  value={this.basedOn}
                  onChange={this.onBasedChanged}
                >
                  <option></option>
                  {this.availableTypes.map((type, index) => (
                    <option
                      key={index}
                      value={type.type}
                      className="text large"
                    >
                      {type.name}
                    </option>
                  ))}
                </StruxhubSelect>
              </div>
            </div>
            {this.workflowDiagram}
          </div>
          <DialogFooter className="tw:sm:justify-center">
            <Button
              disabled={
                !(!!this.typeName && !!this.categoryId && !!this.basedOn)
              }
              className="text extra-large tw:max-w-1/2 tw:grow"
              variant="default"
              onClick={this.onDialogSubmit}
            >
              {Localization.translator.submit_verb}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  private get workflowDiagram(): JSX.Element {
    if (!this.selectedBasedType) {
      return null
    }

    const {
      inspectionFrequency,
      inspectionOptions,
      workflowSteps,
      isAutoActivationEnabled,
    } = this.selectedBasedType

    return (
      <>
        <div className="col x-center my12">
          <span className="text extra-large bold mb10">
            {Localization.translator.workflow}
          </span>
        </div>
        <div className="col mb20">
          <div className="pb10 row overflow-auto">
            <div className="row x-center">
              <PermitWorkflowDiagram
                workflowSteps={workflowSteps}
                isAutoActivationEnabled={isAutoActivationEnabled}
                inspectionFrequency={inspectionFrequency}
                inspectionFrequencyType={
                  inspectionOptions?.inspectionFrequencyType
                }
              />
            </div>
          </div>
        </div>
      </>
    )
  }

  @action.bound
  private onDialogSubmit() {
    this.props.store.createCustomPermitType(
      this.typeName,
      this.categoryId,
      this.selectedBasedType,
    )
  }

  @action.bound
  private onNameChanged(event: React.ChangeEvent<HTMLInputElement>) {
    this.typeName = event.target.value
  }

  @action.bound
  private onNameReset() {
    this.typeName = EMPTY_STRING
  }

  @action.bound
  private onCategoryChanged(event: React.ChangeEvent<HTMLSelectElement>) {
    this.categoryId = event.target.value
  }

  @action.bound
  private onBasedChanged(event: React.ChangeEvent<HTMLSelectElement>) {
    this.basedOn = event.target.value
  }

  @computed
  private get selectedBasedType(): WorkflowType {
    return this.availableTypes.find(({ type }) => this.basedOn === type)
  }

  @computed
  private get availableTypes(): WorkflowType[] {
    return this.store.permitTypes.filter(t => !t.isNonCustomizable) || []
  }
}
